import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { SleepSound } from '@parentpath/supabase-client';

export interface DurationOption {
  label: string;
  seconds: number; // 0 = unlimited
}

export const DURATION_OPTIONS: DurationOption[] = [
  { label: '15min', seconds: 15 * 60 },
  { label: '30min', seconds: 30 * 60 },
  { label: '45min', seconds: 45 * 60 },
  { label: '1h', seconds: 60 * 60 },
  { label: '1.5h', seconds: 90 * 60 },
  { label: '2h', seconds: 2 * 60 * 60 },
  { label: 'unlimited', seconds: 0 },
];

export const FADE_OUT_OPTIONS = [
  { label: '5 Min', seconds: 5 * 60 },
  { label: '10 Min', seconds: 10 * 60 },
  { label: '15 Min', seconds: 15 * 60 },
];

const FADE_IN_MS = 3000;
const FADE_IN_STEP_MS = 50;

export function useSleepSounds() {
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedDurationIndex, setSelectedDurationIndex] = useState(1); // 30min default
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [fadeOutEnabled, setFadeOutEnabled] = useState(false);
  const [fadeOutDurationIndex, setFadeOutDurationIndex] = useState(0); // 5min default
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeInIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const fadeOutRef = useRef({ enabled: false, seconds: 5 * 60 });
  const fadeInCompleteRef = useRef(true);

  // Keep fade-out ref in sync with state
  useEffect(() => {
    fadeOutRef.current = {
      enabled: fadeOutEnabled,
      seconds: FADE_OUT_OPTIONS[fadeOutDurationIndex].seconds,
    };
  }, [fadeOutEnabled, fadeOutDurationIndex]);

  // Auto-correct fade-out when playback duration changes
  useEffect(() => {
    const playbackSeconds = DURATION_OPTIONS[selectedDurationIndex].seconds;
    if (playbackSeconds === 0) {
      // Unlimited - fade-out not possible
      setFadeOutEnabled(false);
      return;
    }
    setFadeOutDurationIndex((prev) => {
      if (FADE_OUT_OPTIONS[prev].seconds >= playbackSeconds) {
        const firstValid = FADE_OUT_OPTIONS.findIndex((opt) => opt.seconds < playbackSeconds);
        return firstValid >= 0 ? firstValid : 0;
      }
      return prev;
    });
  }, [selectedDurationIndex]);

  const { data: sounds = [], isLoading } = useQuery({
    queryKey: ['sleep-sounds'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sleep_sounds')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as SleepSound[];
    },
  });

  const currentSound = sounds[currentIndex] || null;

  const getPublicUrl = useCallback((bucket: string, path: string) => {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }, []);

  const clearFadeIn = useCallback(() => {
    if (fadeInIntervalRef.current) {
      clearInterval(fadeInIntervalRef.current);
      fadeInIntervalRef.current = null;
    }
  }, []);

  const stopPlayback = useCallback(async () => {
    clearFadeIn();

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Update session with actual duration
    if (sessionIdRef.current && startTimeRef.current && user) {
      const actualSeconds = Math.round(
        (Date.now() - startTimeRef.current.getTime()) / 1000
      );
      await supabase
        .from('sleep_sound_sessions')
        .update({
          stopped_at: new Date().toISOString(),
          actual_duration_seconds: actualSeconds,
        })
        .eq('id', sessionIdRef.current);
    }

    sessionIdRef.current = null;
    startTimeRef.current = null;
    fadeInCompleteRef.current = true;
    setIsPlaying(false);
    setRemainingSeconds(null);
  }, [user, clearFadeIn]);

  const startPlayback = useCallback(async () => {
    if (!currentSound) return;

    const soundUrl = getPublicUrl('SleepSounds', currentSound.sound_path);
    const duration = DURATION_OPTIONS[selectedDurationIndex];

    // Create or reuse audio element
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = true;
    }
    audioRef.current.src = soundUrl;

    // Start with volume 0 for fade-in
    audioRef.current.volume = 0;
    fadeInCompleteRef.current = false;

    try {
      await audioRef.current.play();
    } catch (err) {
      console.error('Failed to play audio:', err);
      return;
    }

    // 3s fade-in
    const fadeInStart = Date.now();
    clearFadeIn();
    fadeInIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - fadeInStart;
      const progress = Math.min(elapsed / FADE_IN_MS, 1);
      if (audioRef.current) {
        audioRef.current.volume = progress;
      }
      if (progress >= 1) {
        clearFadeIn();
        fadeInCompleteRef.current = true;
      }
    }, FADE_IN_STEP_MS);

    setIsPlaying(true);
    startTimeRef.current = new Date();

    // Record session
    if (user) {
      const { data: session } = await supabase
        .from('sleep_sound_sessions')
        .insert({
          user_id: user.id,
          sleep_sound_id: currentSound.id,
          duration_seconds: duration.seconds,
        })
        .select('id')
        .single();
      if (session) {
        sessionIdRef.current = session.id;
      }
    }

    // Timer countdown (if not unlimited)
    if (duration.seconds > 0) {
      setRemainingSeconds(duration.seconds);
      timerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev === null || prev <= 1) {
            stopPlayback();
            return null;
          }
          const newVal = prev - 1;

          // Fade-out: gradually reduce volume in the last X minutes
          const fo = fadeOutRef.current;
          if (fo.enabled && fo.seconds > 0 && fadeInCompleteRef.current && audioRef.current) {
            if (newVal <= fo.seconds) {
              audioRef.current.volume = Math.max(0, newVal / fo.seconds);
            }
          }

          return newVal;
        });
      }, 1000);
    }
  }, [currentSound, selectedDurationIndex, getPublicUrl, user, stopPlayback, clearFadeIn]);

  const goToNext = useCallback(() => {
    if (sounds.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % sounds.length);
  }, [sounds.length]);

  const goToPrev = useCallback(() => {
    if (sounds.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + sounds.length) % sounds.length);
  }, [sounds.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (fadeInIntervalRef.current) {
        clearInterval(fadeInIntervalRef.current);
      }
    };
  }, []);

  return {
    sounds,
    isLoading,
    currentIndex,
    currentSound,
    isPlaying,
    selectedDurationIndex,
    setSelectedDurationIndex,
    remainingSeconds,
    fadeOutEnabled,
    setFadeOutEnabled,
    fadeOutDurationIndex,
    setFadeOutDurationIndex,
    startPlayback,
    stopPlayback,
    goToNext,
    goToPrev,
    setCurrentIndex,
    getPublicUrl,
  };
}
